import * as React from 'react';
const { Fragment, useState, useCallback } = React;

import {
	getImageSize,
	compressImage,
	convertFileToBase64,
} from '../../utils/file';
import Box from '../box';
import Button from '../button';
import ConfirmButton from '../confirm-button';
import FileDropzone from '../file-dropzone';
import Dialog from '../dialog';
import {
	Root,
	ImageStyled,
	ImageFallback,
} from './styled';


export type ImageUploaderProps = {
	value: string;
	width?: number;
	height?: number;
	uploadLabel?: string;
	removeLabel?: string;
	useBase64?: boolean;
	onChange: (e: React.SyntheticEvent, previewURL: string, files: Array<File>) => void;
}

const ImageUploader: React.SFC<ImageUploaderProps> = (props) => {
	const {
		value,
		width,
		height,
		uploadLabel,
		removeLabel,
		useBase64,
		onChange,
	} = props;
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [files, setFiles] = useState<Array<File>>([]);
	const handleOpen = useCallback(() => setIsOpen(true), []);
	const handleClose = useCallback(() => setIsOpen(false), []);
	const handleChange = useCallback((value: Array<File>) => setFiles(value), []);

	const handleUpload = useCallback((e: React.SyntheticEvent) => {
		const file = files[0];
		const previewURL = URL.createObjectURL(file);
	
		if (useBase64) {
			getImageSize(previewURL)
				.then(({ width, height }) => compressImage({ width, height, file, quality: 0.2, scale: 0.5 }))
				.then(convertFileToBase64)
				.then(previewURL => {
					handleClose();
					onChange(e, previewURL, files);
				});	
		} else {	
			handleClose();
			onChange(e, previewURL, files);
		}
	}, [files]);

	const handleRemove = useCallback((e: React.SyntheticEvent) => {
		onChange(e, '', []);
	}, []);

	return (
		<Root>
			{
				value
				? <Fragment>
						<ImageStyled src={value} width={width} height={height} />
						<Box display='flex' justifyContent='center' marginTop='6px'>
							<ConfirmButton
								appearance='text'
								size='small'
								onClick={handleRemove}>
								{removeLabel}
							</ConfirmButton>
						</Box>
					</Fragment>
				: <Fragment>
						<ImageFallback
							width={width}
							height={height}
							onClick={handleOpen}
						/>
						<Box display='flex' justifyContent='center' marginTop='10px'>
							<Button
								appearance='text'
								size='small'
								onClick={handleOpen}>
								{uploadLabel}
							</Button>
						</Box>
					</Fragment>
			}
			<Dialog.Base
				isOpen={isOpen}
				onClose={handleClose}>
				<Dialog.Header onClose={handleClose}>Загрузка изображения</Dialog.Header>
				<Dialog.Content style={{ width: 400 }}>
					<FileDropzone
						accept={['image/jpg', 'image/jpeg', 'image/png', 'image/tif', 'image/tiff']}
						limit={1}
						renderDragActiveContent={() => <div>Перенесите изображение на эту область</div>}
						onChange={handleChange}
					/>
				</Dialog.Content>
				<Dialog.Footer display='flex' justifyContent='center'>
					<Button
						disabled={files.length === 0}
						onClick={handleUpload}>
						Загрузить
					</Button>
				</Dialog.Footer>
			</Dialog.Base>
		</Root>
	);
}

ImageUploader.displayName = 'ImageUploader';
ImageUploader.defaultProps = {
	width: 85,
	height: 85,
	uploadLabel: 'Загрузить',
	removeLabel: 'Удалить',
};

export default ImageUploader;
